import ApiService from '@/services/ApiService';
import AuthService, { FirebaseError } from '@/services/AuthService';
import AuthorizationService from '@/services/AuthorizationService';
import reactQueryClient from '@/services/ReactQueryClientService';
import { setUserAndAuthToken } from '@/utils/auth';
import { auth } from '@/utils/firebase';
import { logError, logInfo } from '@/utils/logger';
import { ApplicationVerifier, ConfirmationResult, User } from 'firebase/auth';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
type AuthContextType = {
  user: User | null;
  error: FirebaseError | null;
  checkingAuthStatus: boolean;
  setUser: (user: User | null) => void;
  signInWithGoogle: () => Promise<void>;
  signInWithTwitter: () => Promise<void>;
  signInWithFacebook: () => Promise<void>;
  signInWithPhoneNumber: (
    phoneNumber: string,
    appVerifier: ApplicationVerifier,
  ) => Promise<ConfirmationResult>;
  signUpWithEmailAndPassword: (
    email: string,
    password: string,
  ) => Promise<void>;
  signInWithEmailAndPassword: (
    email: string,
    password: string,
  ) => Promise<void>;
  signOut: () => Promise<void>;
  confirmOTPCode: (
    otpCode: string,
    confirmationResult: ConfirmationResult,
  ) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  verifyPasswordResetCode: (oobCode: string) => Promise<void>;
  confirmPasswordReset: (email: string, newPassword: string) => Promise<void>;
  getAuthProviderDetails: () => Promise<Record<string, string>>;
  isEmailVerified: () => Promise<boolean>;
  resendVerificationEmail: () => Promise<boolean>;
  applyActionCode: (oobCode: string) => Promise<boolean>;
};
const AuthContext = createContext<AuthContextType>({} as AuthContextType);
export const useAuth = () => useContext(AuthContext);
export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<FirebaseError | null>(null);
  const [checkingAuthStatus, setCheckingStatus] = useState<boolean>(true);
  const handleAuthError = useCallback((error: any) => {
    logError(error);
    const firebaseError = error as FirebaseError;
    setError(firebaseError);
    throw error;
  }, []);
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user: any) => {
      try {
        if (user) {
          await setUserAndAuthToken(user, setUser);
          ApiService.startTokenRefreshInterval();
          logInfo(
            'Token refresh interval started',
            'AuthProvider.onAuthStateChanged',
          );
          await AuthorizationService.checkAuthorizationStatus();
          logInfo(
            'Authorization status checked after login',
            'AuthProvider.onAuthStateChanged',
          );
        } else {
          setUser(null);
          sessionStorage.removeItem('authToken');
          ApiService.stopTokenRefreshInterval();
          logInfo(
            'Token refresh interval stopped',
            'AuthProvider.onAuthStateChanged',
          );
        }
        setCheckingStatus(false);
      } catch (error) {
        handleAuthError(error);
      }
    });
    return () => {
      unsubscribe();
      ApiService.stopTokenRefreshInterval();
    };
  }, [handleAuthError]);
  const signInWithGoogle = useCallback(async () => {
    try {
      const user = await AuthService.signInWithGoogle();
      await setUserAndAuthToken(user, setUser);
    } catch (error) {
      handleAuthError(error);
    }
  }, [handleAuthError]);
  const signInWithTwitter = useCallback(async () => {
    try {
      const user = await AuthService.signInWithTwitter();
      await setUserAndAuthToken(user, setUser);
    } catch (error) {
      handleAuthError(error);
    }
  }, [handleAuthError]);
  const signInWithFacebook = useCallback(async () => {
    try {
      const user = await AuthService.signInWithFacebook();
      await setUserAndAuthToken(user, setUser);
    } catch (error) {
      handleAuthError(error);
    }
  }, [handleAuthError]);
  const signInWithPhoneNumber = useCallback(
    async (
      phoneNumber: string,
      appVerifier: ApplicationVerifier,
    ): Promise<ConfirmationResult> => {
      try {
        const result = await AuthService.signInWithPhoneNumber(
          phoneNumber,
          appVerifier,
        );
        if (!result) {
          throw new Error('Failed to sign in with phone number.');
        }
        return result;
      } catch (error) {
        handleAuthError(error);
        throw error;
      }
    },
    [handleAuthError],
  );
  const signUpWithEmailAndPassword = useCallback(
    async (email: string, password: string) => {
      try {
        const user = await AuthService.signUp(email, password);
        await setUserAndAuthToken(user, setUser);
      } catch (error) {
        handleAuthError(error);
      }
    },
    [handleAuthError],
  );
  const isEmailVerified = useCallback(() => {
    return AuthService.isEmailVerified();
  }, []);
  const resendVerificationEmail = useCallback(async () => {
    try {
      return await AuthService.resendVerificationEmail();
    } catch (error) {
      handleAuthError(error);
      return false;
    }
  }, [handleAuthError]);
  const applyActionCode = useCallback(
    async (oobCode: string) => {
      try {
        return await AuthService.applyActionCode(oobCode);
      } catch (error) {
        handleAuthError(error);
        return false;
      }
    },
    [handleAuthError],
  );
  const signInWithEmailAndPassword = useCallback(
    async (email: string, password: string) => {
      try {
        const user = await AuthService.loginWithEmailAndPassword(
          email,
          password,
        );
        await setUserAndAuthToken(user, setUser);
      } catch (error) {
        handleAuthError(error);
      }
    },
    [handleAuthError],
  );
  const signOut = useCallback(async () => {
    try {
      ApiService.stopTokenRefreshInterval();
      setUser(null);
      localStorage.clear();
      sessionStorage.clear();
      reactQueryClient.clear();
      AuthorizationService.setAuthorized();
      await AuthService.logout();
    } catch (error) {
      handleAuthError(error);
    }
  }, [handleAuthError]);
  const confirmOTPCode = useCallback(
    async (otpCode: string, confirmationResult: ConfirmationResult) => {
      try {
        const user = await AuthService.confirmOTPCode(
          otpCode,
          confirmationResult,
        );
        await setUserAndAuthToken(user, setUser);
      } catch (error) {
        handleAuthError(error);
      }
    },
    [handleAuthError],
  );
  const resetPassword = useCallback(
    async (email: string) => {
      try {
        await AuthService.resetPassword(email);
      } catch (error) {
        handleAuthError(error);
      }
    },
    [handleAuthError],
  );
  const verifyPasswordResetCode = useCallback(
    async (oobCode: string) => {
      try {
        await AuthService.verifyCode(oobCode);
      } catch (error) {
        handleAuthError(error);
      }
    },
    [handleAuthError],
  );
  const confirmPasswordReset = useCallback(
    async (oobCode: string, newPassword: string) => {
      try {
        await AuthService.confirmPassword(oobCode, newPassword);
      } catch (error) {
        handleAuthError(error);
      }
    },
    [handleAuthError],
  );
  const getAuthProviderDetails = useCallback(async () => {
    const tokenData = await user?.getIdTokenResult();
    const identities = tokenData?.claims.firebase?.identities;
    const signInProviderName = tokenData?.signInProvider;
    if (identities && signInProviderName) {
      let signInId;
      let providerValue = '';
      switch (signInProviderName) {
        case 'phone':
          signInId = identities?.['phone'];
          providerValue = signInId ? signInId[0] : '';
          break;
        case 'twitter.com':
          const providerData = user?.providerData.find(
            (provider) => provider.providerId === 'twitter.com',
          );
          providerValue = providerData?.displayName || '';
          providerValue =
            `${localStorage.getItem('twitterScreenName')}` ||
            providerData?.email ||
            providerData?.displayName ||
            '';
          break;
        default:
          signInId = identities?.['email'];
          providerValue = signInId ? signInId[0] : '';
          break;
      }
      return {
        providerName: signInProviderName,
        providerValue,
      };
    }
    return { providerName: '', providerValue: '' };
  }, [user]);
  const value = useMemo(
    () => ({
      user,
      error,
      checkingAuthStatus,
      setUser,
      signInWithGoogle,
      signInWithTwitter,
      signInWithFacebook,
      signInWithPhoneNumber,
      signUpWithEmailAndPassword,
      signInWithEmailAndPassword,
      signOut,
      confirmOTPCode,
      resetPassword,
      verifyPasswordResetCode,
      confirmPasswordReset,
      getAuthProviderDetails,
      isEmailVerified,
      resendVerificationEmail,
      applyActionCode,
    }),
    [
      user,
      error,
      checkingAuthStatus,
      signInWithGoogle,
      signInWithTwitter,
      signInWithFacebook,
      signInWithPhoneNumber,
      signUpWithEmailAndPassword,
      signInWithEmailAndPassword,
      signOut,
      confirmOTPCode,
      resetPassword,
      verifyPasswordResetCode,
      confirmPasswordReset,
      getAuthProviderDetails,
      isEmailVerified,
      resendVerificationEmail,
      applyActionCode,
    ],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
